import { finder } from '@medv/finder';
import getXPath from 'get-xpath';
import { NextStepButtonType } from '../../features/demo/dto/FrameTooltipDTO';
import {
  printGuideLog,
  summarizePathsForLog
} from '../../features/user-guide/helpers/guideLogHelper';
import { isGuideRecoveryDisabled } from '../../features/user-guide/helpers/guideRecoveryHelper';
import { MessageTypes } from '../../features/user-guide/types/MessageTypes';

const whitelistedDefaultAttributes = ['src', 'href'];

const buildFindLogKey = (
  paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[]
) =>
  paths
    .map(
      p =>
        `${p.xpath?.slice(0, 48) ?? ''}:${p.cssPath?.[0]?.slice(0, 64) ?? ''}:${
          p.iframe ? 'if' : ''
        }:${p.shadowRoot ? 'sh' : ''}`
    )
    .join('||');

let lastVerboseFindKey = '';
let lastVerboseFindAt = 0;
let lastByElementResultLogAt = 0;
let lastVisibilityDetailLogAt = 0;
let lastSplitIframeLogAt = 0;
let lastOverallByPathResultLogAt = 0;

const shouldLogFindVerbose = (key: string): boolean => {
  const now = Date.now();
  if (key !== lastVerboseFindKey) {
    lastVerboseFindKey = key;
    lastVerboseFindAt = now;
    return true;
  }
  if (now - lastVerboseFindAt > 2000) {
    lastVerboseFindAt = now;
    return true;
  }
  return false;
};

const htmlElementAttributes: Record<string, string[]> = {
  '*': [
    'accesskey',
    'autocapitalize',
    'autofocus',
    'class',
    'contenteditable',
    'dir',
    'draggable',
    'enterkeyhint',
    'hidden',
    'id',
    'inert',
    'inputmode',
    'is',
    'itemid',
    'itemprop',
    'itemref',
    'itemscope',
    'itemtype',
    'lang',
    'nonce',
    'popover',
    'slot',
    'spellcheck',
    'style',
    'tabindex',
    'title',
    'translate'
  ],
  a: [
    'charset',
    'coords',
    'download',
    'href',
    'hreflang',
    'name',
    'ping',
    'referrerpolicy',
    'rel',
    'rev',
    'shape',
    'target',
    'type'
  ],
  applet: [
    'align',
    'alt',
    'archive',
    'code',
    'codebase',
    'height',
    'hspace',
    'name',
    'object',
    'vspace',
    'width'
  ],
  area: [
    'alt',
    'coords',
    'download',
    'href',
    'hreflang',
    'nohref',
    'ping',
    'referrerpolicy',
    'rel',
    'shape',
    'target',
    'type'
  ],
  audio: ['autoplay', 'controls', 'crossorigin', 'loop', 'muted', 'preload', 'src'],
  base: ['href', 'target'],
  basefont: ['color', 'face', 'size'],
  blockquote: ['cite'],
  body: ['alink', 'background', 'bgcolor', 'link', 'text', 'vlink'],
  br: ['clear'],
  button: [
    'disabled',
    'form',
    'formaction',
    'formenctype',
    'formmethod',
    'formnovalidate',
    'formtarget',
    'name',
    'popovertarget',
    'popovertargetaction',
    'type',
    'value'
  ],
  canvas: ['height', 'width'],
  caption: ['align'],
  col: ['align', 'char', 'charoff', 'span', 'valign', 'width'],
  colgroup: ['align', 'char', 'charoff', 'span', 'valign', 'width'],
  data: ['value'],
  del: ['cite', 'datetime'],
  details: ['open'],
  dialog: ['open'],
  dir: ['compact'],
  div: ['align'],
  dl: ['compact'],
  embed: ['height', 'src', 'type', 'width'],
  fieldset: ['disabled', 'form', 'name'],
  font: ['color', 'face', 'size'],
  form: [
    'accept',
    'accept-charset',
    'action',
    'autocomplete',
    'enctype',
    'method',
    'name',
    'novalidate',
    'target'
  ],
  frame: [
    'frameborder',
    'longdesc',
    'marginheight',
    'marginwidth',
    'name',
    'noresize',
    'scrolling',
    'src'
  ],
  frameset: ['cols', 'rows'],
  h1: ['align'],
  h2: ['align'],
  h3: ['align'],
  h4: ['align'],
  h5: ['align'],
  h6: ['align'],
  head: ['profile'],
  hr: ['align', 'noshade', 'size', 'width'],
  html: ['manifest', 'version'],
  iframe: [
    'align',
    'allow',
    'allowfullscreen',
    'allowpaymentrequest',
    'allowusermedia',
    'frameborder',
    'height',
    'loading',
    'longdesc',
    'marginheight',
    'marginwidth',
    'name',
    'referrerpolicy',
    'sandbox',
    'scrolling',
    'src',
    'srcdoc',
    'width'
  ],
  img: [
    'align',
    'alt',
    'border',
    'crossorigin',
    'decoding',
    'fetchpriority',
    'height',
    'hspace',
    'ismap',
    'loading',
    'longdesc',
    'name',
    'referrerpolicy',
    'sizes',
    'src',
    'srcset',
    'usemap',
    'vspace',
    'width'
  ],
  input: [
    'accept',
    'align',
    'alt',
    'autocomplete',
    'checked',
    'dirname',
    'disabled',
    'form',
    'formaction',
    'formenctype',
    'formmethod',
    'formnovalidate',
    'formtarget',
    'height',
    'ismap',
    'list',
    'max',
    'maxlength',
    'min',
    'minlength',
    'multiple',
    'name',
    'pattern',
    'placeholder',
    'popovertarget',
    'popovertargetaction',
    'readonly',
    'required',
    'size',
    'src',
    'step',
    'type',
    'usemap',
    'value',
    'width'
  ],
  ins: ['cite', 'datetime'],
  isindex: ['prompt'],
  label: ['for', 'form'],
  legend: ['align'],
  li: ['type', 'value'],
  link: [
    'as',
    'blocking',
    'charset',
    'color',
    'crossorigin',
    'disabled',
    'fetchpriority',
    'href',
    'hreflang',
    'imagesizes',
    'imagesrcset',
    'integrity',
    'media',
    'referrerpolicy',
    'rel',
    'rev',
    'sizes',
    'target',
    'type'
  ],
  map: ['name'],
  menu: ['compact'],
  meta: ['charset', 'content', 'http-equiv', 'media', 'name', 'scheme'],
  meter: ['high', 'low', 'max', 'min', 'optimum', 'value'],
  object: [
    'align',
    'archive',
    'border',
    'classid',
    'codebase',
    'codetype',
    'data',
    'declare',
    'form',
    'height',
    'hspace',
    'name',
    'standby',
    'type',
    'typemustmatch',
    'usemap',
    'vspace',
    'width'
  ],
  ol: ['compact', 'reversed', 'start', 'type'],
  optgroup: ['disabled', 'label'],
  option: ['disabled', 'label', 'selected', 'value'],
  output: ['for', 'form', 'name'],
  p: ['align'],
  param: ['name', 'type', 'value', 'valuetype'],
  pre: ['width'],
  progress: ['max', 'value'],
  q: ['cite'],
  script: [
    'async',
    'blocking',
    'charset',
    'crossorigin',
    'defer',
    'fetchpriority',
    'integrity',
    'language',
    'nomodule',
    'referrerpolicy',
    'src',
    'type'
  ],
  select: ['autocomplete', 'disabled', 'form', 'multiple', 'name', 'required', 'size'],
  slot: ['name'],
  source: ['height', 'media', 'sizes', 'src', 'srcset', 'type', 'width'],
  style: ['blocking', 'media', 'type'],
  table: [
    'align',
    'bgcolor',
    'border',
    'cellpadding',
    'cellspacing',
    'frame',
    'rules',
    'summary',
    'width'
  ],
  tbody: ['align', 'char', 'charoff', 'valign'],
  td: [
    'abbr',
    'align',
    'axis',
    'bgcolor',
    'char',
    'charoff',
    'colspan',
    'headers',
    'height',
    'nowrap',
    'rowspan',
    'scope',
    'valign',
    'width'
  ],
  textarea: [
    'autocomplete',
    'cols',
    'dirname',
    'disabled',
    'form',
    'maxlength',
    'minlength',
    'name',
    'placeholder',
    'readonly',
    'required',
    'rows',
    'wrap'
  ],
  tfoot: ['align', 'char', 'charoff', 'valign'],
  th: [
    'abbr',
    'align',
    'axis',
    'bgcolor',
    'char',
    'charoff',
    'colspan',
    'headers',
    'height',
    'nowrap',
    'rowspan',
    'scope',
    'valign',
    'width'
  ],
  thead: ['align', 'char', 'charoff', 'valign'],
  time: ['datetime'],
  tr: ['align', 'bgcolor', 'char', 'charoff', 'valign'],
  track: ['default', 'kind', 'label', 'src', 'srclang'],
  ul: ['compact', 'type'],
  video: [
    'autoplay',
    'controls',
    'crossorigin',
    'height',
    'loop',
    'muted',
    'playsinline',
    'poster',
    'preload',
    'src',
    'width'
  ]
};

function isHTMLElement(node: Node | null) {
  return node?.nodeType === 1;
}

function findNthChildSelector(element: HTMLElement) {
  const siblings = Array.from(element.parentElement?.children ?? []);
  let selector = element.tagName.toLowerCase();
  if (siblings.length > 1) {
    const index = siblings.indexOf(element);
    selector += `:nth-child(${index + 1})`;
  }
  return selector;
}

function isAttributeAllowed(attributeName: string, tagName: string) {
  if (whitelistedDefaultAttributes.includes(attributeName)) {
    return true;
  }
  const globalAttrs = htmlElementAttributes['*'] ?? [];
  const tagAttrs = htmlElementAttributes[tagName.toLowerCase()] ?? [];
  const isStandard = globalAttrs.includes(attributeName) || tagAttrs.includes(attributeName);

  return !isStandard;
}

function isValidAttributeValue(value: string | null) {
  if (value == null || value.length === 0) {
    return false;
  }
  const disallowed = ['null', 'undefined', 'false', 'true'];
  const hasThreeDigits = /\d{3}/.test(value);
  return !disallowed.includes(value) && !hasThreeDigits;
}

export function findCSSPaths(
  { element, root }: { element: HTMLElement; root?: Element },
  childSegment = '',
  accumulator: string[] = [],
  depth = 0
): string[] {
  const MAX_DEPTH = 5;

  let selector = finder(element, {
    root: root ?? (element.ownerDocument as unknown as Element),
    seedMinLength: 1,
    threshold: 1000,
    className: (name: string) => {
      // `hydrated` é estado de runtime do Stencil, não identidade: o componente só a recebe
      // quando hidrata, e a extensão ainda carimba a classe nos que faltavam durante a
      // captura. Como @medv/finder prefere classe a tagName, ela roubaria o lugar de um
      // nome de tag estável por um seletor que casa com todo componente da página — e que
      // pode nem existir no site ao vivo quando o guia rodar. Espelha o mesmo filtro da
      // extensão (get-demo-extension/src/content/helpers/finder.ts).
      return (
        name !== 'hydrated' &&
        !name.includes('get-demo') &&
        !name.includes('single-file') &&
        !name.includes('getdemo-')
      );
    },
    attr(attrName: string, attrValue: string) {
      return (
        !attrName.includes('get-demo') &&
        !attrName.includes('single-file') &&
        !attrName.includes('getdemo-') &&
        !!element?.tagName &&
        isAttributeAllowed(attrName, element.tagName) &&
        isValidAttributeValue(attrValue)
      );
    }
  });

  if (childSegment && selector) {
    selector = `${selector} > ${childSegment}`;
  }

  const willRecurse =
    isHTMLElement(element?.parentElement) &&
    depth < MAX_DEPTH &&
    !!selector &&
    (isHTMLElement(element.parentElement?.parentElement ?? null) || isInShadow(element));

  if (willRecurse) {
    let nthChild = findNthChildSelector(element);
    if (childSegment) {
      nthChild = `${nthChild} > ${childSegment}`;
    }
    return findCSSPaths(
      { element: element.parentElement as HTMLElement, root },
      nthChild,
      accumulator.concat(selector),
      depth + 1
    );
  }

  return selector ? accumulator.concat(selector) : accumulator;
}

const isInShadow = (node: ParentNode | null) => {
  for (; node; node = node.parentNode) {
    if (node.toString() === '[object ShadowRoot]') {
      return true;
    }
  }
  return false;
};

const findTextXPath = (element: Element): string | null => {
  try {
    const inner = element.innerHTML;

    if (!inner.includes('<') && inner.length) {
      return `//${element.tagName}[text()="${inner}"]`;
    }

    return null;
  } catch (error) {
    return null;
  }
};

const findFullXPath = (element: Element): string => {
  return getXPath(element);
};

const getPaths = (
  element: HTMLElement
): {
  xpath: string;
  cssPath: string[];
  textXPath: string | null;
  shadowRoot?: boolean;
  iframe?: boolean;
}[] => {
  const paths = [];
  let currentElement: HTMLElement = element;

  while (currentElement) {
    if (currentElement && isInShadow(currentElement)) {
      const rootNode = currentElement.getRootNode() as ShadowRoot;
      const host = rootNode.host as HTMLElement;
      paths.unshift({
        xpath: findFullXPath(currentElement as HTMLElement),
        textXPath: findTextXPath(currentElement as HTMLElement),
        cssPath: findCSSPaths({
          element: currentElement as HTMLElement,
          root: rootNode as unknown as Element
        }),
        shadowRoot: true
      });
      currentElement = host;
    } else if (
      currentElement &&
      currentElement.ownerDocument.defaultView?.frameElement &&
      !['get-demo-player', 'get-demo-secondary-player'].includes(
        currentElement.ownerDocument.defaultView?.frameElement.id
      )
    ) {
      paths.unshift({
        xpath: findFullXPath(currentElement as HTMLElement),
        textXPath: findTextXPath(currentElement as HTMLElement),
        cssPath: findCSSPaths({
          element: currentElement as HTMLElement,
          root: currentElement.ownerDocument as unknown as Element
        }),
        iframe: true
      });
      currentElement = currentElement.ownerDocument.defaultView?.frameElement as HTMLElement;
    } else {
      paths.unshift({
        xpath: findFullXPath(currentElement as HTMLElement),
        cssPath: findCSSPaths({
          element: currentElement as HTMLElement
        }),
        textXPath: findTextXPath(currentElement as HTMLElement)
      });
      return paths;
    }
  }
  return paths;
};

const MAX_IFRAME_DEPTH = 5;

/**
 * Documentos dos iframes acessíveis (mesma origem) abaixo de `root`, incluindo
 * aninhados. Usado quando o primeiro item do caminho vem marcado `iframe` mas a
 * captura não gravou a cadeia iframe→elemento.
 */
const collectIframeDocuments = (root: Document, depth = 0): Document[] => {
  if (depth >= MAX_IFRAME_DEPTH) return [];
  const docs: Document[] = [];
  for (const frame of Array.from(root.querySelectorAll('iframe'))) {
    let frameDoc: Document | null = null;
    try {
      frameDoc = (frame as HTMLIFrameElement).contentDocument;
    } catch {
      frameDoc = null; // cross-origin: inacessível
    }
    if (!frameDoc) continue;
    docs.push(frameDoc);
    docs.push(...collectIframeDocuments(frameDoc, depth + 1));
  }
  return docs;
};

export function findElementByPathsInDocument(
  paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[],
  rootDocument: Document,
  considerIframes = false
): HTMLElement | null {
  const findLogKey = buildFindLogKey(paths);
  const verbose = shouldLogFindVerbose(findLogKey);
  if (verbose) {
    printGuideLog({
      message: '[Finder] findElementByPathsInDocument:start',
      data: {
        considerIframes,
        segments: paths.length,
        pathSummary: summarizePathsForLog(paths),
        rootIsSameDocument: rootDocument === document
      }
    });
  }

  let currentDocument: Document | ShadowRoot = rootDocument;
  let currentElement: HTMLElement | null = null;

  for (let i = 0; i < paths.length; i++) {
    const path = paths[i];
    const isLastPath = i === paths.length - 1;
    const docKind =
      currentDocument instanceof Document
        ? 'document'
        : currentDocument.toString?.() === '[object ShadowRoot]'
        ? 'shadowRoot'
        : 'unknown';

    const tryFindElement = (): { element: HTMLElement | null; attempts: unknown[] } => {
      const attempts: unknown[] = [];
      if (path.cssPath && path.cssPath.length > 0) {
        for (const cssPath of path.cssPath) {
          try {
            const matches = currentDocument.querySelectorAll(cssPath);
            if (matches.length > 0) {
              const picked = pickVisibleMatch(matches);
              attempts.push({
                strategy: 'css',
                ok: true,
                cssHead: cssPath.slice(0, 160),
                total: picked.total,
                considered: picked.considered,
                visibleCount: picked.visibleCount,
                pickedIndex: picked.pickedIndex,
                fallback: picked.fallback
              });
              if (picked.element) {
                return { element: picked.element, attempts };
              }
            } else {
              attempts.push({
                strategy: 'css',
                ok: false,
                cssHead: cssPath.slice(0, 160),
                total: 0
              });
            }
          } catch (error) {
            attempts.push({
              strategy: 'css',
              ok: false,
              cssHead: cssPath.slice(0, 160),
              error: String(error)
            });
            console.warn('Error in findElementByPathsInDocument', error);
          }
        }
      }

      if (path.textXPath && currentDocument instanceof Document) {
        try {
          const result = currentDocument.evaluate(
            path.textXPath,
            currentDocument,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null
          );
          const element = result.singleNodeValue as HTMLElement;
          attempts.push({ strategy: 'textXPath', ok: Boolean(element) });
          if (element) {
            return { element, attempts };
          }
        } catch (error) {
          attempts.push({ strategy: 'textXPath', ok: false, error: String(error) });
          console.warn('Error in findElementByPathsInDocument', error);
        }
      }

      if (currentDocument instanceof Document) {
        try {
          const result = currentDocument.evaluate(
            path.xpath,
            currentDocument,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null
          );
          const element = result.singleNodeValue as HTMLElement;
          attempts.push({
            strategy: 'xpath',
            ok: Boolean(element),
            xpathHead: path.xpath ? path.xpath.slice(0, 120) : ''
          });
          if (element) {
            return { element, attempts };
          }
        } catch (error) {
          attempts.push({
            strategy: 'xpath',
            ok: false,
            xpathHead: path.xpath ? path.xpath.slice(0, 120) : '',
            error: String(error)
          });
          console.warn('Error in findElementByPathsInDocument', error);
        }
      } else {
        attempts.push({
          strategy: 'xpath',
          skipped: true,
          reason: 'not a Document'
        });
      }

      return { element: null, attempts };
    };

    // A captura grava elemento de iframe com UM item de caminho já marcado
    // `iframe`, sem o item do <iframe> antes. Nesse caso procura dentro dos
    // iframes; o documento raiz fica por último, caso a flag esteja errada.
    const searchDocuments: Array<Document | ShadowRoot> =
      i === 0 && considerIframes && path.iframe
        ? [...collectIframeDocuments(rootDocument), rootDocument]
        : [currentDocument];

    let found: HTMLElement | null = null;
    const attempts: unknown[] = [];
    for (const searchDocument of searchDocuments) {
      currentDocument = searchDocument;
      const result = tryFindElement();
      attempts.push(...result.attempts);
      if (result.element) {
        found = result.element;
        break;
      }
    }
    currentElement = found;

    if (verbose || !currentElement) {
      printGuideLog({
        message: '[Finder] findElementByPathsInDocument:segment',
        data: {
          segmentIndex: i,
          isLastPath,
          docKind,
          attempts,
          found: Boolean(currentElement),
          tag: currentElement?.tagName
        }
      });
    }

    if (!currentElement) {
      printGuideLog({
        message: '[Finder] findElementByPathsInDocument:miss',
        data: { segmentIndex: i, isLastPath, findLogKey }
      });
      return null;
    }

    if (!isLastPath && paths[i + 1]?.shadowRoot) {
      const shadowRoot = currentElement.shadowRoot;
      if (!shadowRoot) {
        printGuideLog({
          message: '[Finder] findElementByPathsInDocument:noShadowRoot',
          data: { segmentIndex: i, tag: currentElement.tagName },
          level: 'warn'
        });
        return null;
      }
      currentDocument = shadowRoot;
    } else if (
      considerIframes &&
      !isLastPath &&
      paths[i + 1]?.iframe &&
      (currentElement as HTMLIFrameElement).contentDocument
    ) {
      const frameDoc = (currentElement as HTMLIFrameElement).contentDocument;
      currentDocument = frameDoc as Document | ShadowRoot;
      if (verbose) {
        printGuideLog({
          message: '[Finder] findElementByPathsInDocument:enteredIframeDoc',
          data: { segmentIndex: i }
        });
      }
    }
  }

  if (verbose) {
    printGuideLog({
      message: '[Finder] findElementByPathsInDocument:hit',
      data: { tag: currentElement?.tagName, id: currentElement?.id }
    });
  }

  return currentElement;
}

function findElementByPaths(
  paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[]
): HTMLElement | null {
  return findElementByPathsInDocument(paths, document);
}

const splitPathsByIframe = (
  paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[]
): {
  mainPaths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[];
  framePaths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[];
  frameElement?: HTMLIFrameElement | null;
} => {
  const iframeIndex = paths.findIndex(path => path.iframe === true);

  if (iframeIndex === -1) {
    return {
      mainPaths: [...paths],
      framePaths: []
    };
  } else {
    const mainPaths = paths.slice(0, iframeIndex);
    const framePaths = paths.slice(iframeIndex);
    const frameElement = findElementByPaths(mainPaths) as HTMLIFrameElement;

    const splitNow = Date.now();
    if (splitNow - lastSplitIframeLogAt > 850) {
      lastSplitIframeLogAt = splitNow;
      printGuideLog({
        message: '[Finder] splitPathsByIframe:iframeSegment',
        data: {
          iframeIndex,
          mainSegments: mainPaths.length,
          frameSegments: framePaths.length,
          resolvedFrame: Boolean(frameElement),
          frameTag: frameElement?.tagName,
          frameId: frameElement?.id,
          frameSrcHead: (frameElement as HTMLIFrameElement)?.src?.slice(0, 120)
        }
      });
    }

    if (!frameElement) {
      printGuideLog({
        message: '[Finder] splitPathsByIframe:frameElementUnresolved',
        data: { pathSummary: summarizePathsForLog(mainPaths) },
        level: 'warn'
      });
    }

    const firstFramePath = framePaths.slice(0, 1);
    const restFramePaths = framePaths.slice(1);
    return {
      mainPaths,
      framePaths: [{ ...firstFramePath[0], iframe: false }, ...restFramePaths],
      frameElement
    };
  }
};

const MAX_CSS_CANDIDATES = 5;

const findContainingBlock = (element: HTMLElement): HTMLElement | null => {
  const style = window.getComputedStyle(element);
  if (style.position === 'fixed') {
    let ancestor: HTMLElement | null = element.parentElement;
    while (ancestor) {
      const s = window.getComputedStyle(ancestor);
      if (
        s.transform !== 'none' ||
        s.perspective !== 'none' ||
        s.filter !== 'none' ||
        s.willChange === 'transform'
      ) {
        return ancestor;
      }
      ancestor = ancestor.parentElement;
    }
    return null;
  }
  if (style.position === 'absolute') {
    let ancestor: HTMLElement | null = element.parentElement;
    while (ancestor) {
      const s = window.getComputedStyle(ancestor);
      if (
        s.position !== 'static' ||
        s.transform !== 'none' ||
        s.perspective !== 'none' ||
        s.filter !== 'none'
      ) {
        return ancestor;
      }
      ancestor = ancestor.parentElement;
    }
    return null;
  }
  return element.parentElement;
};

const escapesOverflowClipping = (element: HTMLElement, overflowAncestor: HTMLElement): boolean => {
  let current: HTMLElement | null = element;
  let depth = 0;
  while (current && current !== overflowAncestor && depth++ < 50) {
    const style = window.getComputedStyle(current);
    if (style.position === 'fixed' || style.position === 'absolute') {
      const cb = findContainingBlock(current);
      if (!cb) return true;
      if (cb === overflowAncestor) return false;
      if (!overflowAncestor.contains(cb)) return true;
      current = cb;
      continue;
    }
    current = current.parentElement;
  }
  return false;
};

export const isElementVisibleCheap = (el: Element | null | undefined): boolean => {
  if (!el || !(el as HTMLElement).isConnected) return false;
  const htmlEl = el as HTMLElement;

  const rect = htmlEl.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;

  const style = window.getComputedStyle(htmlEl);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }

  let parent = htmlEl.parentElement;
  while (parent && parent !== document.body) {
    const ps = window.getComputedStyle(parent);
    if (ps.visibility === 'hidden' || ps.opacity === '0') {
      return false;
    }
    if (ps.display === 'contents') {
      parent = parent.parentElement;
      continue;
    }
    if (ps.display === 'none') {
      return false;
    }
    if (ps.overflow === 'hidden' || ps.overflowX === 'hidden' || ps.overflowY === 'hidden') {
      if (!escapesOverflowClipping(htmlEl, parent)) {
        const er = htmlEl.getBoundingClientRect();
        const pr = parent.getBoundingClientRect();
        if (
          er.left >= pr.right ||
          er.right <= pr.left ||
          er.top >= pr.bottom ||
          er.bottom <= pr.top
        ) {
          return false;
        }
      }
    }
    parent = parent.parentElement;
  }
  return true;
};

export const pickVisibleMatch = (
  matches: NodeListOf<Element>
): {
  element: HTMLElement | null;
  total: number;
  considered: number;
  visibleCount: number;
  pickedIndex: number;
  fallback: boolean;
} => {
  const total = matches.length;
  if (total === 0) {
    return {
      element: null,
      total: 0,
      considered: 0,
      visibleCount: 0,
      pickedIndex: -1,
      fallback: false
    };
  }
  if (total === 1) {
    return {
      element: matches[0] as HTMLElement,
      total: 1,
      considered: 0,
      visibleCount: 0,
      pickedIndex: 0,
      fallback: false
    };
  }
  const considered = Math.min(total, MAX_CSS_CANDIDATES);
  let firstVisibleIndex = -1;
  let visibleCount = 0;
  for (let k = 0; k < considered; k++) {
    if (isElementVisibleCheap(matches[k])) {
      visibleCount++;
      if (firstVisibleIndex === -1) firstVisibleIndex = k;
    }
  }
  if (firstVisibleIndex >= 0) {
    return {
      element: matches[firstVisibleIndex] as HTMLElement,
      total,
      considered,
      visibleCount,
      pickedIndex: firstVisibleIndex,
      fallback: false
    };
  }
  return {
    element: matches[0] as HTMLElement,
    total,
    considered,
    visibleCount: 0,
    pickedIndex: 0,
    fallback: true
  };
};

const isElementVisible = (element?: HTMLElement): boolean => {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return false;
  }

  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }

  let parent = element.parentElement;
  while (parent && parent !== document.body) {
    const parentStyle = window.getComputedStyle(parent);

    if (parentStyle.display === 'contents') {
      parent = parent.parentElement;
      continue;
    }

    if (
      parentStyle.overflow === 'hidden' ||
      parentStyle.overflowX === 'hidden' ||
      parentStyle.overflowY === 'hidden'
    ) {
      if (!escapesOverflowClipping(element, parent)) {
        const elementRect = element.getBoundingClientRect();
        const parentOffset = parent.getBoundingClientRect();

        if (
          elementRect.left >= parentOffset.right ||
          elementRect.right <= parentOffset.left ||
          elementRect.top >= parentOffset.bottom ||
          elementRect.bottom <= parentOffset.top
        ) {
          return false;
        }
      }
    }

    parent = parent.parentElement;
  }

  if (isElementFullyCoveredWithShadow(element)) {
    return false;
  }

  return true;
};

const buildVisibilityChain = (
  target: Element
): Array<{ root: Document | ShadowRoot; node: Element }> => {
  const chain: Array<{ root: Document | ShadowRoot; node: Element }> = [];
  let node: Element = target;
  let root: Document | ShadowRoot = node.getRootNode() as Document | ShadowRoot;

  while (true as any) {
    chain.unshift({ root, node });
    if (root instanceof ShadowRoot) {
      node = root.host;
      root = node.getRootNode() as Document | ShadowRoot;
    } else {
      break;
    }
  }

  return chain;
};

const pointIsVisible = (
  chain: Array<{ root: Document | ShadowRoot; node: Element }>,
  x: number,
  y: number
): boolean => {
  for (const { root, node } of chain) {
    let topElem: Element | null = null;

    if (typeof (root as any).elementsFromPoint === 'function') {
      const elems = (root as any).elementsFromPoint(x, y) as Element[];
      if (!elems || elems.length === 0) return false;

      topElem = elems.find(elem => !elem.hasAttribute('getdemo-checklist-player')) || elems[0];
    } else if (typeof (root as any).elementFromPoint === 'function') {
      topElem = (root as any).elementFromPoint(x, y) as Element | null;
      if (!topElem) return false;

      if (topElem.hasAttribute('getdemo-checklist-player')) {
        const allElements = (root as any).elementsFromPoint?.(x, y) as Element[];
        if (allElements) {
          topElem =
            allElements.find(elem => !elem.hasAttribute('getdemo-checklist-player')) || topElem;
        }
      }
    } else {
      return false;
    }

    if (topElem !== node && !node.contains(topElem)) {
      return false;
    }
  }

  return true;
};

const isElementFullyCoveredWithShadow = (element: Element, samplesX = 5, samplesY = 5): boolean => {
  const rects = Array.from(element.getClientRects());
  if (!rects.length) return true;

  const chain = buildVisibilityChain(element);

  for (const rect of rects) {
    const stepX = rect.width / samplesX;
    const stepY = rect.height / samplesY;
    if (stepX <= 0 || stepY <= 0) continue;

    for (let i = 0; i < samplesX; i++) {
      const x = rect.left + stepX * (i + 0.5);
      for (let j = 0; j < samplesY; j++) {
        const y = rect.top + stepY * (j + 0.5);
        if (pointIsVisible(chain, x, y)) {
          return false;
        }
      }
    }
  }

  return true;
};

const isElementOutOfViewport = (dimensions: {
  x: number;
  y: number;
  width: number;
  height: number;
}): boolean => {
  const rect = {
    left: dimensions.x,
    top: dimensions.y,
    right: dimensions.x + dimensions.width,
    bottom: dimensions.y + dimensions.height
  };
  return (
    rect.bottom < 0 ||
    rect.top > window.innerHeight ||
    rect.right < 0 ||
    rect.left > window.innerWidth
  );
};

interface ElementDimensions {
  x: number;
  y: number;
  width: number;
  height: number;
  containerWidth: number;
  containerHeight: number;
  element?: HTMLElement;
  isOutOfViewport?: boolean;
  frameElement?: HTMLIFrameElement;
  isElementVisible?: boolean;
}

interface ElementDimensionsOptions {
  onElementFound?: (element: HTMLElement) => void;
  onElementLost?: () => void;
  onBeforeFind?: () => void;
  isOutOfViewport?: boolean;
}

const getElementDimensions = (element?: HTMLElement): ElementDimensions | null => {
  if (!element || !element.isConnected) {
    printGuideLog({
      message: '[Finder] getElementDimensions:noElement',
      data: { hasElement: Boolean(element), connected: element?.isConnected ?? false }
    });
    return null;
  }
  const { x, y, width, height } = element?.getBoundingClientRect() || {
    x: 0,
    y: 0,
    width: 0,
    height: 0
  };

  const isOutOfViewport = isElementOutOfViewport({ x, y, width, height });
  const isElementVisibleValue = isElementVisible(element);
  const style = window.getComputedStyle(element);

  if (!isElementVisibleValue || isOutOfViewport) {
    const visNow = Date.now();
    if (visNow - lastVisibilityDetailLogAt > 700) {
      lastVisibilityDetailLogAt = visNow;
      printGuideLog({
        message: '[Finder] getElementDimensions:visibility',
        data: {
          tag: element.tagName,
          id: element.id,
          rect: { x, y, width, height },
          isOutOfViewport,
          isElementVisible: isElementVisibleValue,
          display: style.display,
          visibility: style.visibility,
          opacity: style.opacity,
          fullyCovered: isElementFullyCoveredWithShadow(element)
        }
      });
    }
  }

  return {
    x,
    y,
    width,
    height,
    containerWidth: window.innerWidth,
    containerHeight: window.innerHeight,
    element,
    isOutOfViewport,
    isElementVisible: isElementVisibleValue
  };
};

const OBSERVER_STABILITY_CHECK_MS = 60;
const OBSERVER_STABILITY_CHECKS = 2;

const confirmStableElement = async (
  paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[]
): Promise<HTMLElement | null> => {
  let element: HTMLElement | null = null;
  for (let check = 0; check < OBSERVER_STABILITY_CHECKS; check++) {
    await new Promise(resolve => setTimeout(resolve, OBSERVER_STABILITY_CHECK_MS));
    element = findElementByPaths(paths) as HTMLElement | null;
    if (!element || !element.isConnected) return null;
  }
  return element;
};

const waitForElement = (
  paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[],
  options: ElementDimensionsOptions = {}
): Promise<HTMLElement | null> => {
  return new Promise(async resolve => {
    options.onBeforeFind?.();
    const element = findElementByPaths(paths);

    if (element) {
      printGuideLog({
        message: '[Finder] waitForElement:immediateHit',
        data: { tag: element.tagName, pathSummary: summarizePathsForLog(paths) }
      });
      options.onElementFound?.(element);
      resolve(element);
      return;
    } else {
      printGuideLog({
        message: '[Finder] waitForElement:watchingDom',
        data: { pathSummary: summarizePathsForLog(paths) }
      });
      options.onElementLost?.();
    }

    let stabilityCheckInFlight = false;
    let settled = false;

    const observer = new MutationObserver(() => attemptResolve());

    const settle = (found: HTMLElement) => {
      settled = true;
      observer.disconnect();
      options.onElementFound?.(found);
      resolve(found);
    };

    const attemptResolve = () => {
      if (settled || stabilityCheckInFlight) return;
      const candidate = findElementByPaths(paths);
      if (!candidate) return;

      if (isGuideRecoveryDisabled()) {
        printGuideLog({
          message: '[Finder] waitForElement:observerHit',
          data: { tag: candidate.tagName, stabilized: false }
        });
        settle(candidate);
        return;
      }

      stabilityCheckInFlight = true;
      confirmStableElement(paths).then(stable => {
        stabilityCheckInFlight = false;
        if (settled) return;
        if (!stable) {
          printGuideLog({
            message: '[Finder] waitForElement:observerHitUnstable',
            data: { tag: candidate.tagName, pathSummary: summarizePathsForLog(paths) },
            level: 'warn'
          });
          setTimeout(attemptResolve, OBSERVER_STABILITY_CHECK_MS * 2);
          return;
        }
        printGuideLog({
          message: '[Finder] waitForElement:observerHit',
          data: { tag: stable.tagName, stabilized: true }
        });
        settle(stable);
      });
    };

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });
  });
};

const getElementDimensionsByPath = async (
  paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[],
  options: ElementDimensionsOptions = {}
): Promise<ElementDimensions | null> => {
  const element = await waitForElement(paths, options);
  return getElementDimensions(element as HTMLElement);
};

const getOverallElementDimensionsByPath = async (
  paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[],
  options: ElementDimensionsOptions = {},
  nextStepButtonType: NextStepButtonType | null = null
) => {
  const { mainPaths, framePaths, frameElement } = splitPathsByIframe(paths);

  let dimensions = (await getElementDimensionsByPath(mainPaths, options)) as ElementDimensions;
  if (!dimensions) {
    printGuideLog({
      message: '[Finder] getOverallElementDimensionsByPath:noBaseDimensions',
      data: { pathSummary: summarizePathsForLog(mainPaths) },
      level: 'warn'
    });
    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      containerWidth: window.innerWidth,
      containerHeight: window.innerHeight,
      isElementVisible: false,
      isOutOfViewport: false
    } as ElementDimensions;
  }
  if (frameElement) {
    const hasContentWindow = Boolean(frameElement.contentWindow);
    printGuideLog({
      message: '[Finder] getOverallElementDimensionsByPath:iframePostMessage',
      data: {
        hasContentWindow,
        framePathsSummary: summarizePathsForLog(framePaths)
      }
    });
    const frameDimensions = (await new Promise(resolve => {
      frameElement.contentWindow?.postMessage(
        {
          type: MessageTypes.GET_IFRAME_ELEMENT_DIMENSIONS,
          paths: framePaths,
          nextStepButtonType
        },
        '*'
      );
      const messageHandler = async (event: MessageEvent) => {
        if (event.data.type === MessageTypes.SET_IFRAME_ELEMENT_DIMENSIONS) {
          window.removeEventListener('message', messageHandler);
          resolve(event.data.dimensions);
        }
      };
      window.addEventListener('message', messageHandler);
    })) as any;
    const iframeShellVisible = dimensions.isElementVisible;
    dimensions = {
      x: dimensions.x + frameDimensions.x,
      y: dimensions.y + frameDimensions.y,
      width: frameDimensions.width,
      height: frameDimensions.height,
      containerWidth: dimensions.containerWidth,
      containerHeight: dimensions.containerHeight,
      element: dimensions.element,
      frameElement: frameElement as HTMLIFrameElement | undefined,
      isElementVisible: frameDimensions.isElementVisible && iframeShellVisible
    };
    printGuideLog({
      message: '[Finder] getOverallElementDimensionsByPath:iframeMerged',
      data: {
        iframeShellVisible,
        innerVisible: frameDimensions.isElementVisible,
        combinedVisible: dimensions.isElementVisible,
        frameDims: {
          x: frameDimensions.x,
          y: frameDimensions.y,
          width: frameDimensions.width,
          height: frameDimensions.height
        }
      }
    });
  }
  dimensions.isOutOfViewport = isElementOutOfViewport(dimensions);
  const pathResNow = Date.now();
  if (pathResNow - lastOverallByPathResultLogAt > 850) {
    lastOverallByPathResultLogAt = pathResNow;
    printGuideLog({
      message: '[Finder] getOverallElementDimensionsByPath:result',
      data: {
        x: dimensions.x,
        y: dimensions.y,
        width: dimensions.width,
        height: dimensions.height,
        isOutOfViewport: dimensions.isOutOfViewport,
        isElementVisible: dimensions.isElementVisible,
        hasFrameElement: Boolean(dimensions.frameElement)
      }
    });
  }
  return dimensions;
};

const getOverallElementDimensionsByElement = async (
  element: HTMLElement,
  paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[]
) => {
  if (!element) return;
  const { framePaths, frameElement } = splitPathsByIframe(paths);

  let dimensions = getElementDimensions(element);
  if (!dimensions) {
    printGuideLog({
      message: '[Finder] getOverallElementDimensionsByElement:noBaseDimensions',
      data: { pathSummary: summarizePathsForLog(paths) }
    });
    return null;
  }
  if (frameElement) {
    printGuideLog({
      message: '[Finder] getOverallElementDimensionsByElement:iframePostMessage',
      data: { framePathsSummary: summarizePathsForLog(framePaths) }
    });
    const frameDimensions = (await new Promise(resolve => {
      frameElement.contentWindow?.postMessage(
        {
          type: MessageTypes.GET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT,
          paths: framePaths
        },
        '*'
      );
      const messageHandler = async (event: MessageEvent) => {
        if (event.data.type === MessageTypes.SET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT) {
          window.removeEventListener('message', messageHandler);
          resolve(event.data.dimensions);
        }
      };
      window.addEventListener('message', messageHandler);
    })) as any;
    if (!frameDimensions) {
      printGuideLog({
        message: '[Finder] getOverallElementDimensionsByElement:noFrameDimensionsReply',
        level: 'warn'
      });
      return null;
    }
    const iframeShellVisible = dimensions.isElementVisible;
    dimensions = {
      x: dimensions.x + frameDimensions.x,
      y: dimensions.y + frameDimensions.y,
      width: frameDimensions.width,
      height: frameDimensions.height,
      containerWidth: dimensions.containerWidth,
      containerHeight: dimensions.containerHeight,
      isOutOfViewport:
        isElementOutOfViewport({
          x: dimensions.x + frameDimensions.x,
          y: dimensions.y + frameDimensions.y,
          width: frameDimensions.width,
          height: frameDimensions.height
        }) ||
        dimensions.isOutOfViewport ||
        frameDimensions.isOutOfViewport,
      isElementVisible: frameDimensions.isElementVisible && iframeShellVisible
    };
    printGuideLog({
      message: '[Finder] getOverallElementDimensionsByElement:iframeMerged',
      data: {
        iframeShellVisible,
        innerVisible: frameDimensions.isElementVisible,
        combinedVisible: dimensions.isElementVisible
      }
    });
  }

  const now = Date.now();
  if (now - lastByElementResultLogAt > 900) {
    lastByElementResultLogAt = now;
    printGuideLog({
      message: '[Finder] getOverallElementDimensionsByElement:result',
      data: {
        x: dimensions.x,
        y: dimensions.y,
        width: dimensions.width,
        height: dimensions.height,
        isOutOfViewport: dimensions.isOutOfViewport,
        isElementVisible: dimensions.isElementVisible
      }
    });
  }

  return dimensions;
};

const deepElementFromPoint = (root: DocumentOrShadowRoot, x: number, y: number): Element | null => {
  let currentRoot: DocumentOrShadowRoot = root;
  let lastElement: Element | null = null;
  let iterationGuard = 0;
  const MAX_ITERATIONS = 200;

  while (iterationGuard++ < MAX_ITERATIONS) {
    const elements = currentRoot.elementsFromPoint(x, y);
    const filteredElement = elements.filter(
      elem => !elem.hasAttribute('data-get-demo-click-capture')
    );
    const element = filteredElement[0];

    if (!element || element === lastElement) {
      return element || lastElement;
    }

    lastElement = element;

    const shadowRoot = (element as HTMLElement).shadowRoot;
    if (shadowRoot) {
      currentRoot = shadowRoot;
      continue;
    }

    break;
  }

  return lastElement;
};

const Finder = {
  getPaths,
  findElementByPaths,
  isElementVisible,
  isElementFullyCoveredWithShadow,
  isElementOutOfViewport,
  getElementDimensions,
  getElementDimensionsByPath,
  waitForElement,
  splitPathsByIframe,
  getOverallElementDimensionsByPath,
  getOverallElementDimensionsByElement,
  isInShadow,
  deepElementFromPoint
};

export default Finder;
